import React, { Fragment } from "react";
import {
  PlxsPopover,
  PlxsRow,
  PlxsHeading,
  PlxsText,
  PlxsButton,
} from "@plexlogic/plexus-ui";

interface DeleteConfirmationPopoverProps {
  /** The HTML element to anchor the popover to */
  anchorElement: HTMLElement | null;
  /** Whether the popover is open */
  isOpen: boolean;
  /** Title of the item being deleted */
  itemName?: string;
  /** Custom confirmation message */
  message?: string;
  /** Callback when user confirms deletion */
  onConfirm: () => void;
  /** Callback when user cancels or clicks away */
  onCancel: () => void;
  /** Loading state during deletion */
  isDeleting?: boolean;
  /** Data test ID for testing */
  dataTestId?: string;
}

/**
 * DeleteConfirmationPopover Component
 *
 * A reusable popover component for confirming delete actions.
 * Follows Plexus UI design system with proper spacing and tone.
 * 
 * Performance Optimizations:
 * - Memoized to prevent unnecessary re-renders
 * - Early return pattern for null anchor element
 * - Uses React.Fragment to avoid extra DOM nodes
 *
 * @example
 * ```tsx
 * <DeleteConfirmationPopover
 *   anchorElement={buttonRef}
 *   isOpen={isOpen}
 *   itemName="Plexus Random Draw"
 *   message="This action cannot be undone."
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 * />
 * ```
 */
const DeleteConfirmationPopover: React.FC<DeleteConfirmationPopoverProps> = ({
  anchorElement,
  isOpen,
  itemName = "Item",
  message = "This action cannot be undone.",
  onConfirm,
  onCancel,
  isDeleting = false,
  dataTestId = "delete-confirmation-popover",
}) => {
  // Early return to avoid rendering when not needed
  // Must check both isOpen AND anchorElement to prevent rendering in top-left corner
  if (!isOpen || !anchorElement) {
    return null;
  }

  return (
    <PlxsPopover
      anchorElement={anchorElement}
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClickAway={onCancel}
    >
      <Fragment>
        <PlxsRow spaceBelow="md">
          <PlxsHeading element="h2">Archive {itemName}</PlxsHeading>
        </PlxsRow>
        <PlxsRow spaceBelow="md">
          <PlxsText element="span">
            Archived draws can still be accessed through the user activity log.
          </PlxsText>
        </PlxsRow>
        <PlxsRow spaceBetween="md">
          <PlxsButton
            dataTestId={`${dataTestId}--confirm-button`}
            label={isDeleting ? "Archiving..." : `Archive`}
            onClick={onConfirm}
            tone="urgent"
            isDisabled={isDeleting}
          />
          <PlxsButton
            dataTestId={`${dataTestId}--cancel-button`}
            label="Cancel"
            onClick={onCancel}
            variant="secondary"
            isDisabled={isDeleting}
          />
        </PlxsRow>
      </Fragment>
    </PlxsPopover>
  );
};

export default React.memo(DeleteConfirmationPopover);
